import React, { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import styles from './styles.module.scss';

type QueueHistoryPoint = {
  timestamp: number;
  size: number;
};

interface SlidingQueueChartProps {
  data: QueueHistoryPoint[];
  queueName: string;
}

export const SlidingQueueChart: React.FC<SlidingQueueChartProps> = ({ data, queueName }) => {
  const chartRef = useRef<any>(null);

  const chartOption = useMemo((): EChartsOption => {
    if (!data || data.length === 0) {
      return {
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: 'center',
            style: {
              text: 'Collecting data...',
              fontSize: 16,
              fontWeight: 'bold',
              fill: '#999',
              fontFamily: 'Ubuntu',
            },
          },
        ],
      };
    }

    const now = Date.now();
    
    // Sort data by timestamp to ensure correct order
    const sortedData = [...data].sort((a, b) => a.timestamp - b.timestamp);
    
    // Calculate relative time labels (seconds ago from now)
    // Since we have exactly 6 points at 10-second intervals, generate labels based on position
    // This ensures unique labels: 50s, 40s, 30s, 20s, 10s, now
    const timeLabels = sortedData.map((point, index) => {
      const totalPoints = sortedData.length;
      const positionFromEnd = totalPoints - 1 - index;
      
      // Most recent point (last in array) shows "now"
      if (positionFromEnd === 0) {
        return 'now';
      }
      
      // Calculate seconds ago based on position (each point is 10 seconds apart)
      const secondsAgo = positionFromEnd * 10;
      return `${secondsAgo}s`;
    });

    // Extract values
    const values = sortedData.map((point) => point.size);

    return {
      animation: true,
      animationDuration: 500,
      animationEasing: 'cubicOut',
      grid: {
        top: '10%',
        bottom: '15%',
        left: '10%',
        right: '10%',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: 'rgba(66, 85, 255, 0.3)',
          },
        },
        textStyle: { fontFamily: 'Ubuntu', fontSize: 14 },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          const dataIndex = param.dataIndex;
          const point = sortedData[dataIndex];
          const date = new Date(point.timestamp).toLocaleTimeString();
          return `${date}<br/>${param.marker} Queue Size: ${point.size}`;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: [8, 12],
        extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border-radius: 4px;',
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: timeLabels,
        axisLabel: {
          show: true,
          fontFamily: 'Ubuntu',
          fontSize: 10,
          color: '#666',
          interval: 0, // Show all labels
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.1)',
            type: 'dashed',
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.1)',
            type: 'dashed',
          },
        },
        axisLabel: {
          show: true,
          fontFamily: 'Ubuntu',
          fontSize: 10,
          formatter: (val: number): string => val.toString(),
        },
      },
      series: [
        {
          name: 'Queue Size',
          type: 'line',
          data: values,
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#4255FF',
            shadowColor: 'rgba(66, 85, 255, 0.3)',
            shadowBlur: 10,
            shadowOffsetY: 5,
          },
          lineStyle: {
            width: 3,
            color: '#4255FF',
            shadowColor: 'rgba(66, 85, 255, 0.3)',
            shadowBlur: 10,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(66, 85, 255, 0.3)' },
                { offset: 1, color: 'rgba(66, 85, 255, 0.05)' },
              ],
            },
            shadowColor: 'rgba(66, 85, 255, 0.2)',
            shadowBlur: 10,
          },
          label: {
            show: true,
            position: 'top',
            fontFamily: 'Ubuntu',
            fontSize: 10,
            formatter: (params: any) => {
              return sortedData[params.dataIndex].size.toString();
            },
          },
          emphasis: {
            itemStyle: {
              color: '#3344FF',
              shadowBlur: 20,
              shadowColor: 'rgba(66, 85, 255, 0.5)',
            },
          },
        },
      ],
    };
  }, [data, queueName]);

  return (
    <div className={styles.slidingChartContainer}>
      <ReactECharts
        key={`sliding-chart-${queueName}`}
        option={chartOption}
        ref={chartRef}
        opts={{ renderer: 'canvas' }}
        style={{ height: '300px', width: '100%' }}
        notMerge={false}
      />
    </div>
  );
};

